import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Search,
    Filter,
    Check,
    X,
    Calendar,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    DollarSign,
    Receipt,
    Download,
    Loader,
    User,
    Briefcase,
    RotateCcw,
    Eye,
} from "lucide-react";
import {
    getManagerNoteDeFraisInboxRequest,
    getManagerNoteDeFraisHistoryRequest,
    managerNoteDeFraisDecisionRequest,
} from "../../../../redux/actions/noteDeFraisRequestActions";
import useTreatmentDelegation from "./useTreatmentDelegation";
import useTreatmentAutoRefresh from "./useTreatmentAutoRefresh";
import { confirmTreatmentApproval } from "./confirmTreatmentApproval";
import { exportTreatmentExcel } from "./exportTreatmentExcel";
import "../TraitementStyles/TraitementNotesFrais.css";
import "../TraitementStyles/TreatmentMesOrdres.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL } from "../../../../../api";

const getExpenseCategories = (note) => {
    const categories = [...new Set((note?.items || []).map(item => item.category).filter(Boolean))];
    return categories.length > 0 ? categories.join(", ") : note?.category || note?.requestType || "-";
};
const getExpenseDescriptions = (note) => {
    const descriptions = (note?.items || []).map(item => item.description).filter(Boolean);
    return descriptions.length > 0 ? descriptions.join(" · ") : note?.description || "-";
};

const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");
    const wrapperRef = useRef(null);

    useEffect(() => {
        setQuery(value || "");
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const handleSelect = (opt) => {
        setQuery(opt.label);
        onChange(opt.value);
        setOpen(false);
    };

    const handleInputChange = (e) => {
        setQuery(e.target.value);
        onChange(e.target.value);
        setOpen(true);
    };

    const handleClear = () => {
        setQuery("");
        onChange("");
        setOpen(false);
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    className="dcInput"
                    style={{ width: "100%", fontSize: "13px", paddingRight: 56 }}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                />
                <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                    {query && (
                        <button type="button" onClick={handleClear} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title={t("Effacer")}>
                            <X size={13} />
                        </button>
                    )}
                    <button type="button" onClick={() => setOpen(o => !o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }} title={t("Afficher la liste")}>
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>
            {open && filteredOptions.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, maxHeight: 220, overflowY: "auto" }}>
                    {filteredOptions.map((opt) => {
                        const isSelected = opt.value === value;
                        return (
                            <div key={opt.value} onClick={() => handleSelect(opt)}
                                style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", background: isSelected ? "#eff6ff" : "#fff", color: isSelected ? "#204385" : "#374151", fontWeight: isSelected ? 600 : 400 }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? "#eff6ff" : "#fff")}>
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
            {open && query && filteredOptions.length === 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.10)", zIndex: 30, padding: "8px 12px", fontSize: 12, color: "#9ca3af" }}>
                    {t("Aucun résultat")}
                </div>
            )}
        </div>
    );
};

const TraitementNotesFraisManager = () => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === "ar" ? "ar-MA" : i18n.language === "en" ? "en-US" : "fr-FR";
    const dispatch = useDispatch();

    // Redux state
    const { managerInbox, managerHistory, loading } = useSelector((state) => state.noteDeFraisRequests);
    const user = useSelector((state) => state.auth.user);

    const [activeTab, setActiveTab] = useState("enCours");
    const [filterCollab, setFilterCollab] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterDate, setFilterDate] = useState("");

    const handleResetFilters = () => {
        setFilterCollab("");
        setFilterType("");
        setFilterDate("");
    };

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const delegation = useTreatmentDelegation({
        endpoint: "/api/note-frais-requests/manager/treatment-delegation",
        label: "de notes de frais",
        onUpdated: () => {
            dispatch(getManagerNoteDeFraisInboxRequest());
            dispatch(getManagerNoteDeFraisHistoryRequest());
        },
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(getManagerNoteDeFraisInboxRequest());
        dispatch(getManagerNoteDeFraisHistoryRequest());
    }, [dispatch]);

    useTreatmentAutoRefresh(() => {
        dispatch(getManagerNoteDeFraisInboxRequest({ silent: true }));
        dispatch(getManagerNoteDeFraisHistoryRequest({ silent: true }));
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        return date.toLocaleDateString(locale, {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant, devise = "MAD") => {
        return new Intl.NumberFormat(locale, {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(montant || 0) + " dhs";
    };

    const getSoldeAvance = (note) => Number(note?.soldeAvance ?? ((Number(note?.avance) || 0) - (Number(note?.montantDepense) || 0)));
    const getLibelleSoldeAvance = (note) => {
        const solde = getSoldeAvance(note);
        return solde > 0 ? t("À rendre aux RH") : solde < 0 ? t("À recevoir des RH") : t("Aucun solde d'avance");
    };
    const getCouleurSoldeAvance = (note) => getSoldeAvance(note) > 0 ? "#b45309" : getSoldeAvance(note) < 0 ? "#0369a1" : "#64748b";

    const getDownloadUrl = (path) =>
        `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path || "")}`;

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "Manager : en attente", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN: { label: "En attente de l'admin RH", bg: "#d1ecf1", color: "#0c5460" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
            PAID: { label: "Remboursé", bg: "#d4edda", color: "#155724" },
        };
        const { label, bg, color } = config[statut] || config.PENDING_MANAGER;
        return (
            <span className="tnfBadge" style={{ backgroundColor: bg, color }}>
                {label}
            </span>
        );
    };

    const getRhStatusBadge = (note) => {
        const rawStatus = String(note.status || "").toUpperCase();
        if (note.managerDecision !== true && !["PENDING_ADMIN", "APPROVED", "REJECTED", "PAID"].includes(rawStatus)) return null;
        const config = {
            PENDING_ADMIN: { label: note.managerDecision == null ? "Déléguée à la RH" : "RH : en attente", bg: "#d1ecf1", color: "#0c5460" },
            approved: { label: "RH : approuvée", bg: "#d4edda", color: "#155724" },
            rejected: { label: "RH : rejetée", bg: "#f8d7da", color: "#721c24" },
        };
        const key = note.adminDecision === true ? "approved" : note.adminDecision === false ? "rejected" : note.status;
        const c = config[key];
        return c ? <span className="tnfBadge" style={{ backgroundColor: c.bg, color: c.color }}>{t(c.label)}</span> : null;
    };

    const handleApprove = (noteId) => {
        confirmTreatmentApproval(() => {
            dispatch(managerNoteDeFraisDecisionRequest({
                id: noteId,
                decision: true,
                comment: "",
            }));

            if (showDetailsModal) closeDetails();

            setTimeout(() => {
                dispatch(getManagerNoteDeFraisInboxRequest());
                dispatch(getManagerNoteDeFraisHistoryRequest());
            }, 800);
        });
    };

    const handleReject = (note) => {
        setSelectedNote(note);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedNote) {
            alert("Veuillez saisir un motif de rejet");
            return;
        }

        dispatch(managerNoteDeFraisDecisionRequest({
            id: selectedNote.id,
            decision: false,
            comment: motifRejet,
        }));

        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedNote(null);

        if (showDetailsModal) {
            closeDetails();
        }

        setTimeout(() => {
            dispatch(getManagerNoteDeFraisInboxRequest());
            dispatch(getManagerNoteDeFraisHistoryRequest());
        }, 1000);
    };

    const showDetails = (note) => {
        setSelectedNote(note);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedNote(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedNote(null);
    };

    // Combiner les données selon l'onglet actif
    const mapNoteToTab = (note) => {
        const validatorStatus = String(note.validatorStatus || "").toUpperCase();
        if (validatorStatus === "APPROVED") return "approuve";
        if (validatorStatus === "REJECTED") return "rejete";
        if (note.managerDecision === true) return "approuve";
        if (note.managerDecision === false) return "rejete";
        if (String(note.status || "").toUpperCase() === "PENDING_ADMIN") return "approuve";
        if (String(note.status || "").toUpperCase() === "APPROVED" || String(note.status || "").toUpperCase() === "PAID") return "approuve";
        if (String(note.status || "").toUpperCase() === "REJECTED") return "rejete";
        if (note.status === "PENDING_MANAGER") return "enCours";
        return null;
    };

    const managerNotes = useMemo(() => {
        const notesParId = new Map();
        [...(managerHistory || []), ...(managerInbox || [])].forEach(note => notesParId.set(note.id, note));
        return Array.from(notesParId.values());
    }, [managerInbox, managerHistory]);

    const allNotes = useMemo(
        () => managerNotes.filter(note => mapNoteToTab(note) === activeTab),
        [activeTab, managerNotes]
    );

    const collaborateurOptions = useMemo(() => {
        const map = new Map();
        (allNotes || []).forEach((n) => {
            const name = n.employeeName || "";
            if (name && !map.has(name)) {
                map.set(name, { value: name, label: n.employeeMatricule ? `${name} (${n.employeeMatricule})` : name });
            }
        });
        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [allNotes]);

    const typeOptions = useMemo(() => {
        return [...new Set((allNotes || []).flatMap((n) => getExpenseCategories(n).split(", ")).filter(Boolean))]
            .sort().map((t) => ({ value: t, label: t }));
    }, [allNotes]);

    const getFilteredNotes = () => {
        let filtered = allNotes;

        if (filterCollab) {
            const val = filterCollab.toLowerCase();
            filtered = filtered.filter((n) =>
                (n.employeeName || "").toLowerCase().includes(val) ||
                (n.employeeMatricule || "").toLowerCase().includes(val)
            );
        }
        if (filterType) {
            const val = filterType.toLowerCase();
            filtered = filtered.filter((n) =>
                getExpenseCategories(n).toLowerCase().includes(val)
            );
        }
        if (filterDate) {
            filtered = filtered.filter((n) => {
                if (!n.dateDepense && !n.createdAt) return false;
                const dStr = n.dateDepense || n.createdAt;
                return String(dStr).slice(0, 10) === filterDate;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        const enCoursCount = managerNotes.filter(note => mapNoteToTab(note) === "enCours").length;
        const approuveCount = managerNotes.filter(note => mapNoteToTab(note) === "approuve").length;
        const rejeteCount = managerNotes.filter(note => mapNoteToTab(note) === "rejete").length;

        return [
            { id: "enCours", label: "En attente", count: enCoursCount },
            { id: "approuve", label: "Approuvées", count: approuveCount },
            { id: "rejete", label: "Rejetées", count: rejeteCount },
        ];
    }, [managerNotes]);

    const typesDepense = useMemo(() => {
        const types = new Set();
        allNotes.forEach(n => {
            getExpenseCategories(n).split(", ").filter(Boolean).forEach(type => types.add(type));
        });
        return Array.from(types);
    }, [allNotes]);

    const totalMontant = useMemo(
        () => getFilteredNotes().reduce((sum, n) => sum + (n.montantDepense || 0), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allNotes, filterCollab, filterType, filterDate]
    );

    const urgentCount = useMemo(() => {
        return managerInbox?.filter(n => n.urgent && n.status === "PENDING_MANAGER").length || 0;
    }, [managerInbox]);

    const filteredNotes = getFilteredNotes();

    const getCouleurByCategorie = (categorie) => {
        const colors = {
            TRANSPORT: "#3b82f6",
            HEBERGEMENT: "#8b5cf6",
            RESTAURATION: "#10b981",
            AUTRE: "#f59e0b",
        };
        return colors[categorie] || "#6b7280";
    };

    if (loading && allNotes.length === 0) {
        return (
            <div className="tnfPage">
                <div className="tnfWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                {/* Header */}
                <div className="tnfHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div>
                        <h1>{t("Traitement des notes de frais")}</h1>
                        <p>{t("Validez les notes de frais de votre équipe")}</p>
                    </div>
                    <div className="tavDelegationSetting">
                        <div>
                            <div className="tavDelegationSetting__title">{t("Délégation Admin RH")}</div><div className="tavDelegationSetting__sub">{t("Transmettre mon traitement des notes de frais à l'admin RH")}</div>
                        </div>
                        <label className="tavSwitch" title={t("Délégation Admin RH")}>
                            <input
                                type="checkbox"
                                checked={delegation.enabled}
                                disabled={delegation.loading || !delegation.loaded}
                                onChange={(e) => delegation.toggle(e.target.checked)}
                            />
                            <span />
                        </label>
                    </div>
                </div>

                {/* Stats */}
                {/*<div className="tnfStats">*/}
                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">En attente</div>*/}
                {/*            <div className="tnfStatValue">*/}
                {/*                {(managerInbox || []).filter(note => note.status === "PENDING_MANAGER").length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <Clock size={28} className="tnfStatIcon tnfStatIcon--warn" />*/}
                {/*    </div>*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Montant total</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--money">{formatMontant(totalMontant)}</div>*/}
                {/*        </div>*/}
                {/*        <DollarSign size={28} className="tnfStatIcon tnfStatIcon--accent" />*/}
                {/*    </div>*/}

                {/*    {urgentCount > 0 && (*/}
                {/*        <div className="tnfStatCard">*/}
                {/*            <div>*/}
                {/*                <div className="tnfStatLabel">Urgentes</div>*/}
                {/*                <div className="tnfStatValue tnfStatValue--orange">{urgentCount}</div>*/}
                {/*            </div>*/}
                {/*            <AlertCircle size={28} className="tnfStatIcon tnfStatIcon--orange" />*/}
                {/*        </div>*/}
                {/*    )}*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Approuvées</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--ok">*/}
                {/*                {managerHistory?.filter(n =>*/}
                {/*                    n.status === "APPROVED" || n.status === "PAID" || n.status === "PENDING_ADMIN"*/}
                {/*                ).length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <Check size={28} className="tnfStatIcon tnfStatIcon--ok" />*/}
                {/*    </div>*/}

                {/*    <div className="tnfStatCard">*/}
                {/*        <div>*/}
                {/*            <div className="tnfStatLabel">Rejetées</div>*/}
                {/*            <div className="tnfStatValue tnfStatValue--no">*/}
                {/*                {managerHistory?.filter(n => n.status === "REJECTED").length || 0}*/}
                {/*            </div>*/}
                {/*        </div>*/}
                {/*        <X size={28} className="tnfStatIcon tnfStatIcon--no" />*/}
                {/*    </div>*/}
                {/*</div>*/}

                {/* Filters Panel */}
                <div className="tnfCard" style={{ overflow: "visible" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "16px", padding: "4px" }}>
                        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>{t("Collaborateur / Matricule")}</label><SearchableCombobox value={filterCollab} onChange={setFilterCollab} options={collaborateurOptions} placeholder={t("Nom ou matricule...")} />
                        </div>
                        <div style={{ minWidth: 200, flex: "1 1 200px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>{t("Type de dépense")}</label><SearchableCombobox value={filterType} onChange={setFilterType} options={typeOptions} placeholder={t("Rechercher un type...")} />
                        </div>
                        <div style={{ minWidth: 160, flex: "1 1 160px" }}>
                            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>{t("Date de demande")}</label>
                            <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="dcInput" style={{ width: "100%", fontSize: "13px" }} />
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "flex-end" }}>
                            <button type="button" className="dcBtn dcBtn--outline treatmentExportBtn" style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", background: "#10b981", color: "white", border: "none", whiteSpace: "nowrap" }} onClick={() => exportTreatmentExcel(filteredNotes, "Notes_de_frais_manager")}><Download size={14} /> {t("Exporter Excel")}</button>
                            <button type="button" onClick={handleResetFilters} className="dcBtn dcBtn--outline" style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", height: "38px", whiteSpace: "nowrap" }}>
                                <RotateCcw size={14} /> {t("Réinitialiser")}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabs + Table */}
                <div className="tnfCard">
                    <div className="tnfTabs">
                        {tabs.map((tab) => (
                            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`tnfTab ${activeTab === tab.id ? "is-active" : ""}`}>
                                {t(tab.label)}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tnfTableWrap">
                        {filteredNotes.length === 0 ? (
                            <div className="tnfEmpty">
                                <AlertCircle size={46} />
                                <div>{t("Aucune note de frais trouvée")}</div>
                            </div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                    <tr>
                                        <th>{t("Collaborateur")}</th><th>{t("Manager direct")}</th><th>{t("Catégorie")}</th><th>{t("Description")}</th>
                                        {/*<th>Centre de coût</th>*/}
                                        <th>{t("Date de la dépense")}</th><th>{t("Montant")}</th><th>{t("Règlement avance")}</th>{activeTab === "rejete" && <th>{t("Motif")}</th>}{activeTab === "approuve" && <th>{t("Statut RH")}</th>}
                                        {/*<th>Statut</th>*/}
                                        <th className="is-right">{t("Actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredNotes.map((note) => (
                                        <tr
                                            key={note.id}
                                            className={`${note.urgent ? "tnfRowUrgent" : ""}`}
                                            title={note.urgent ? t("Note de frais urgente") : ""}
                                        >
                                            <td>
                                                <div className="tnfName">
                                                    <span className="tnfPrimary">
                                                        {note.employeeName || "N/A"}
                                                    </span>
                                                    {note.urgent && <span className="tnfFire">🔥</span>}
                                                </div>
                                                <div className="tnfSub">
                                                    {note.employeeMatricule || "-"}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="tnfType">
                                                    <span
                                                        className="tnfDot"
                                                        style={{ backgroundColor: getCouleurByCategorie(getExpenseCategories(note)) }}
                                                    />
                                                    <span className="tnfPrimary">
                                                        {getExpenseCategories(note)}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="tnfDesc">
                                                <div className="tnfTrunc" title={getExpenseDescriptions(note)}>
                                                    {getExpenseDescriptions(note)}
                                                </div>
                                            </td>

                                            <td><span className="tnfMuted">{formatDate(note.dateDepense)}</span></td>

                                            <td>
                                                <div className="tnfAmount">
                                                    <div className="tnfPrimary tnfAmountMain">{formatMontant(note.montantDepense)}</div>
                                                </div>
                                            </td>
                                            <td><span className="tnfMuted">{note.directManagerName || "—"}</span></td>

                                            <td>
                                                <div className="tnfSub" style={{ color: getCouleurSoldeAvance(note), fontWeight: 700 }}>{getLibelleSoldeAvance(note)}</div>
                                                {getSoldeAvance(note) !== 0 && <div className="tnfPrimary">{formatMontant(Math.abs(getSoldeAvance(note)))}</div>}
                                            </td>

                                            {activeTab === "rejete" && <td><span className="tnfMuted">{note.managerComment || note.adminComment || "—"}</span></td>}
                                            {activeTab === "approuve" && <td>{getRhStatusBadge(note) || "-"}</td>}
                                            {/*<td>{getStatutBadge(note.status)}</td>*/}

                                            <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="tnfRowActions">
                                                    {activeTab === "enCours" && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="tnfActionBtn tnfActionBtn--ok"
                                                                onClick={() => handleApprove(note.id)}
                                                                title={t("Approuver")}
                                                                disabled={loading}
                                                            >
                                                                <Check size={16} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="tnfActionBtn tnfActionBtn--no"
                                                                onClick={() => handleReject(note)}
                                                                title={t("Refuser")}
                                                                disabled={loading}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="tnfIconBtn"
                                                        onClick={() => showDetails(note)}
                                                        title={t("Voir les détails")}
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedNote && (
                <div className="tnfModal">
                    <div className="tnfModal__panel" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon">
                                    <FileText size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">{t("Détails de la note de frais")}</div>
                                    <div className="tnfModal__sub">
                                        {selectedNote.employeeName || "N/A"}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeDetails} aria-label={t("Fermer")}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            {/* Alerte urgence */}
                            {selectedNote.urgent && (
                                <div className="tnfAlert tnfAlert--orange">
                                    <AlertCircle size={18} />
                                    <div>
                                        <div className="tnfAlert__title">{t("Note de frais urgente")}</div><div className="tnfAlert__text">{t("Cette demande nécessite un traitement prioritaire")}</div>
                                    </div>
                                </div>
                            )}

                            {/* Informations collaborateur */}
                            <div className="tnfPanel">
                                <div className="tnfPanel__title">{t("Informations collaborateur")}</div>
                                <div className="tnfGrid2">
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">{t("Nom complet")}</div>
                                        <div className="tnfInfo__value">
                                            {selectedNote.employeeName || "-"}
                                        </div>
                                    </div>
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">{t("Matricule")}</div>
                                        <div className="tnfInfo__value">{selectedNote.employeeMatricule || "-"}</div>
                                    </div>
                                    <div className="tnfInfo">
                                        <div className="tnfInfo__label">{t("ID Employé")}</div>
                                        <div className="tnfInfo__value">{selectedNote.employeeId || "-"}</div>
                                    </div>
                                    {/*<div className="tnfInfo">*/}
                                    {/*    <div className="tnfInfo__label">Centre de coût</div>*/}
                                    {/*    <div className="tnfInfo__value">{selectedNote.centreCouts || "-"}</div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>

                            {/* Détails dépense */}
                            <div className="tnfBlock">
                                <div className="tnfBlock__title">{t("Détails de la dépense")}</div>

                                <div className="tnfLine">
                                    <Receipt size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">{t("Catégorie")}</div>
                                        <div className="tnfLine__value">
                                            {getExpenseCategories(selectedNote)}
                                        </div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <Calendar size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">{t("Date dépense / soumission")}</div>
                                        <div className="tnfLine__value">
                                            {formatDate(selectedNote.dateDepense)} — {t("soumise le")} {formatDate(selectedNote.createdAt)}
                                        </div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <DollarSign size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">{t("Montant demandé")}</div>
                                        <div className="tnfLine__value">{formatMontant(selectedNote.montantDepense)}</div>
                                    </div>
                                </div>

                                <div className="tnfLine">
                                    <DollarSign size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">{t("Règlement de l'avance")}</div>
                                        <div className="tnfLine__value" style={{ color: getCouleurSoldeAvance(selectedNote) }}>
                                            {getLibelleSoldeAvance(selectedNote)}{getSoldeAvance(selectedNote) !== 0 && ` : ${formatMontant(Math.abs(getSoldeAvance(selectedNote)))}`}
                                        </div>
                                    </div>
                                </div>

                                {/*<div className="tnfLine">*/}
                                {/*    <Briefcase size={16} />*/}
                                {/*    <div className="tnfLine__content">*/}
                                {/*        <div className="tnfLine__label">Centre de coût</div>*/}
                                {/*        <div className="tnfLine__value">{selectedNote.centreCouts || "-"}</div>*/}
                                {/*    </div>*/}
                                {/*</div>*/}

                                {selectedNote.ordreMissionLibelle && (
                                    <div className="tnfLine">
                                        <FileText size={16} />
                                        <div className="tnfLine__content">
                                            <div className="tnfLine__label">{t("Ordre de mission")}</div>
                                            <div className="tnfLine__value">
                                                {selectedNote.ordreMissionCode} - {selectedNote.ordreMissionLibelle}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="tnfLine">
                                    <FileText size={16} />
                                    <div className="tnfLine__content">
                                        <div className="tnfLine__label">{t("Description")}</div>
                                        <div className="tnfLine__value">{getExpenseDescriptions(selectedNote)}</div>
                                    </div>
                                </div>
                            </div>

                            {selectedNote.justificatifAvance && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">{t("Justificatif de l'avance")}</div>
                                    <div className="tnfDocs">
                                        <a className="tnfDoc" href={getDownloadUrl(selectedNote.justificatifAvance)} target="_blank" rel="noreferrer">
                                            <FileText size={18} /><div className="tnfDoc__meta"><div className="tnfDoc__name">{selectedNote.justificatifAvance}</div></div><Download size={16} className="tnfDoc__dl" />
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Justificatifs */}
                            {selectedNote.justificatifs && selectedNote.justificatifs.length > 0 && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">{t("Justificatifs")} ({selectedNote.justificatifs.length})</div>
                                    <div className="tnfDocs">
                                        {selectedNote.justificatifs.map((j, idx) => (
                                            <a key={idx} className="tnfDoc" href={getDownloadUrl(j)} target="_blank" rel="noreferrer">
                                                <FileText size={18} />
                                                <div className="tnfDoc__meta">
                                                    <div className="tnfDoc__name">{j}</div>
                                                </div>
                                                <Download size={16} className="tnfDoc__dl" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Historique de validation */}
                            {(selectedNote.status !== "PENDING_MANAGER") && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">{t("Historique de validation")}</div>
                                    <div className="tnfPanel">
                                        <div className="tnfGrid2">
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">{t("Statut")}</div>
                                                <div className="tnfInfo__value">{getStatutBadge(selectedNote.status)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">{t("Date de validation")}</div>
                                                <div className="tnfInfo__value">{formatDate(selectedNote.managerDecisionAt)}</div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">{t("Décision manager")}</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.managerDecision ? (
                                                        <span style={{ color: "#155724" }}>✓ {t("Approuvé")}</span>
                                                    ) : (
                                                        <span style={{ color: "#721c24" }}>✗ {t("Rejeté")}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {selectedNote.managerComment && (
                                            <div className="tnfRejectReason">
                                                <div className="tnfRejectReason__label">{t("Commentaire manager")}</div>
                                                <div className="tnfRejectReason__text">{selectedNote.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Validation RH si applicable */}
                            {(selectedNote.status === "APPROVED" || selectedNote.status === "REJECTED" || selectedNote.status === "PAID") && selectedNote.adminDecisionAt && (
                                <div className="tnfBlock">
                                    <div className="tnfBlock__title">{t("Validation RH")}</div>
                                    <div className="tnfPanel">
                                        <div className="tnfGrid2">
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">{t("Admin RH")}</div>
                                                <div className="tnfInfo__value">
                                                    {selectedNote.adminName || "-"}
                                                </div>
                                            </div>
                                            <div className="tnfInfo">
                                                <div className="tnfInfo__label">{t("Date validation")}</div>
                                                <div className="tnfInfo__value">{formatDate(selectedNote.adminDecisionAt)}</div>
                                            </div>
                                            {selectedNote.montantValide && (
                                                <div className="tnfInfo">
                                                    <div className="tnfInfo__label">{t("Montant validé")}</div>
                                                    <div className="tnfInfo__value tnfValueOk">
                                                        {formatMontant(selectedNote.montantValide)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {selectedNote.adminComment && (
                                            <div className="tnfRejectReason">
                                                <div className="tnfRejectReason__label">{t("Commentaire RH")}</div>
                                                <div className="tnfRejectReason__text">{selectedNote.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="tnfModal__footer">
                            {activeTab === "enCours" && (
                                <>
                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ghostDanger"
                                        onClick={() => {
                                            closeDetails();
                                            handleReject(selectedNote);
                                        }}
                                    >
                                        {t("Rejeter")}
                                    </button>

                                    <button
                                        type="button"
                                        className="tnfBtn tnfBtn--ok"
                                        onClick={() => {
                                            handleApprove(selectedNote.id);
                                            closeDetails();
                                        }}
                                    >
                                        {t("Approuver")}
                                    </button>
                                </>
                            )}

                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeDetails}>
                                {t("Fermer")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* REJECT MODAL */}
            {showRejectModal && selectedNote && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon tnfModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tnfModal__title">{t("Refuser la note de frais")}</div>
                                    <div className="tnfModal__sub">
                                        {selectedNote.employeeName || "N/A"}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tnfIconBtn" onClick={closeReject} aria-label={t("Fermer")}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tnfModal__body">
                            <div className="tnfMiniCard">
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">{t("Catégorie")} :</span>
                                    <span className="tnfStrong">
                                        {getExpenseCategories(selectedNote)}
                                    </span>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">{t("Montant")} :</span>
                                    <span className="tnfStrong tnfStrong--accent">
                                        {formatMontant(selectedNote.montantDepense)}
                                    </span>
                                </div>
                                <div className="tnfMiniRow">
                                    <span className="tnfMuted">{t("Description")} :</span>
                                    <span className="tnfStrong">{getExpenseDescriptions(selectedNote)}</span>
                                </div>

                                {selectedNote.urgent && (
                                    <div className="tnfMiniWarn">
                                        <AlertCircle size={16} />
                                        <span>{t("Note de frais urgente")}</span>
                                    </div>
                                )}
                            </div>

                            <label className="tnfLabel">
                                {t("Motif du rejet")} <span className="tnfReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                required
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder={t("Veuillez préciser le motif du rejet de cette note de frais...")}
                                className="tnfTextarea"
                            />
                            <div className="tnfHelp">{t("Ce motif sera communiqué au collaborateur")}</div>
                        </div>

                        <div className="tnfModal__footer">
                            <button type="button" className="tnfBtn tnfBtn--ghost" onClick={closeReject}>
                                {t("Annuler")}
                            </button>
                            <button
                                type="button"
                                className="tnfBtn tnfBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? t("En cours...") : t("Confirmer le rejet")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementNotesFraisManager;
